import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloudServerOutlined, ReloadOutlined } from '@ant-design/icons';
import { get, post } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import './StorageConfigPage.css';

interface StorageProvider {
  id: number;
  name: string;
  provider_type: string;
  endpoint: string;
  public_endpoint: string;
  access_key: string;
  bucket: string;
  region: string;
  secure: boolean;
  status: number;
  is_default: boolean;
  description: string;
  has_secret_key: boolean;
  updated_at?: string;
}

const providerLabels: Record<string, string> = {
  minio: 'MinIO',
  s3: 'S3兼容',
  custom: '自定义 S3',
  file_service: '平台文件服',
};

const StorageConfigPage: React.FC = () => {
  const [items, setItems] = useState<StorageProvider[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const data = await get<StorageProvider[]>(API_ENDPOINTS.storage.providers);
      setItems(data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载云存储配置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const setDefault = async (record: StorageProvider) => {
    try {
      await post(API_ENDPOINTS.storage.setDefault(record.id));
      message.success(`系统存储已切换到“${record.name}”`);
      fetchProviders();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '切换默认云存储失败');
    }
  };

  const testProvider = async (record: StorageProvider) => {
    try {
      const result = await post<{ ok: boolean; message: string }>(API_ENDPOINTS.storage.test(record.id));
      if (result.ok) {
        message.success(result.message);
      } else {
        message.warning(result.message);
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : '测试云存储失败');
    }
  };

  const columns: ColumnsType<StorageProvider> = [
    {
      title: '名称',
      dataIndex: 'name',
      width: 150,
      render: (value, record) => (
        <Space size={6}>
          <span>{value}</span>
          {record.is_default && <Tag color="green">默认</Tag>}
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'provider_type',
      width: 100,
      render: (value) => <Tag color={value === 'minio' ? 'blue' : value === 'file_service' ? 'cyan' : 'purple'}>{providerLabels[value] || value}</Tag>,
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      render: (value, record) => (
        <div className="storage-endpoint" title={value}>
          {value || (record.provider_type === 'file_service' ? '由部署配置托管（待配置）' : '-')}
        </div>
      ),
    },
    {
      title: 'Bucket',
      dataIndex: 'bucket',
      width: 160,
      render: (value, record) => (record.provider_type === 'file_service' ? '内容寻址' : value),
    },
    {
      title: '状态',
      width: 120,
      render: (_, record) => (
        <Tag color={record.is_default ? 'green' : record.status === 1 ? 'default' : 'orange'}>
          {record.is_default ? '当前启用' : record.status === 1 ? '未启用' : '待配置'}
        </Tag>
      ),
    },
    {
      title: '密钥',
      dataIndex: 'has_secret_key',
      width: 90,
      render: (value) => <Tag color={value ? 'green' : 'red'}>{value ? '已配置' : '未配置'}</Tag>,
    },
    {
      title: '切换',
      width: 180,
      render: (_, record) => (
        <div className="storage-actions">
          <Button size="small" disabled={record.status !== 1} onClick={() => testProvider(record)}>测试</Button>
          <Switch
            checked={record.is_default}
            disabled={record.is_default || record.status !== 1}
            checkedChildren="已启用"
            unCheckedChildren={record.status === 1 ? '启用' : '待配置'}
            onChange={() => setDefault(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="storage-config-page">
      <h1 className="page-title">云存储配置</h1>
      <Alert
        className="storage-summary"
        type="info"
        showIcon
        message="存储参数由当前部署环境的 YAML 配置统一托管，本页面只负责切换和连通性测试。切换后，上传、读取、资源列表、删除和回源统一使用选中的存储。"
      />
      <Card bordered={false}>
        <div className="storage-toolbar">
          <Space>
            <CloudServerOutlined />
            <span>存储方案 {items.length} 个（同时仅启用一个）</span>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchProviders}>刷新</Button>
          </Space>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={false}
          scroll={{ x: 920 }}
        />
      </Card>

    </div>
  );
};

export default StorageConfigPage;
